import React, { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import { useNavigate } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import Loader from '../../Components/GeneralComponents/Loader'
import classes from './index.module.css'

const PAGE_LIMIT = 50
const REFRESH_MS = 5000

// leased == "active" (a worker holds a lease); see JobStatus lifecycle
// pending -> leased -> completed | failed.
const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'leased', label: 'Active' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
]

// attempts increments on every lease (jobs.py). A healthy job is leased
// exactly twice -- once by the router to classify the unrouted row, once by
// the worker to run it -- so 2 is the floor, not a retry. 3+ means a lease
// expired and the job was re-leased (crash / spot preemption).
const LEASES_HELP =
  'A job is normally leased ≤2 times (router classify + worker run) and shows ' +
  'no warning. ⚠ N flags a job leased N≥3 times — re-leased after an expired ' +
  'lease (crashed worker / preempted VM).'

function formatDateTime(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString()
}

function formatRuntime(seconds) {
  if (seconds == null) return '—'
  const s = Number(seconds)
  if (isNaN(s)) return '—'
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  const rem = Math.round(s % 60)
  return `${m}m ${rem}s`
}

// Worker artifacts are written as gs:// URIs (shared/artifacts.py). Rewrite to
// the IAM-authenticated browser URL so an admin already logged into the GCP
// project can click straight through -- no signed-URL machinery needed.
function toBrowsableUri(uri) {
  if (typeof uri !== 'string') return ''
  if (/^https?:\/\//.test(uri)) return uri
  if (uri.startsWith('gs://')) return 'https://storage.cloud.google.com/' + uri.slice(5)
  return ''
}

function artifactEntries(artifact_uris) {
  if (!artifact_uris || typeof artifact_uris !== 'object') return []
  return Object.entries(artifact_uris)
}

function modelRuntimeEntries(model_runtimes) {
  if (!model_runtimes || typeof model_runtimes !== 'object') return []
  return Object.entries(model_runtimes)
}

export default function FeelsSyncJobs() {
  const { getFeelsSyncJobs } = useAPI()
  const navigate = useNavigate()
  const [ref, inView] = useInView()
  const [status, setStatus] = useState('all')

  const {
    data,
    isError,
    error,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['FeelsSyncJobs', status],
    queryFn: ({ pageParam = 1 }) => getFeelsSyncJobs(pageParam, PAGE_LIMIT, status),
    initialPageParam: 1,
    getNextPageParam: pageData =>
      pageData.p * PAGE_LIMIT < pageData.jobs_count ? pageData.p + 1 : undefined,
    refetchInterval: REFRESH_MS,
  })

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Single infinite-scroll trigger: the sentinel intersection observer. Guard
  // on hasNextPage/isFetchingNextPage so the 5s refetch re-renders don't fire
  // redundant fetchNextPage calls while the sentinel stays in view.
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const jobsCount = data?.pages?.[0]?.jobs_count ?? 0

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h1 className={classes.title}>FeelsSync Jobs</h1>
        <div className={classes.tabs}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.id}
              className={`${classes.tab} ${status === tab.id ? classes.tabActive : ''}`}
              onClick={() => setStatus(tab.id)}
            >
              {tab.label}
            </button>
          ))}
          <span className={classes.count}>{jobsCount} jobs</span>
          {isFetching && !isLoading && (
            <span className={classes.refreshing}>updating…</span>
          )}
        </div>
      </div>

      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.th}>Song / Artist</th>
              <th className={classes.th}>Status</th>
              <th className={classes.th}>Tier</th>
              <th className={classes.th}>Lang</th>
              <th className={`${classes.th} ${classes.center}`}>Flags</th>
              <th className={`${classes.th} ${classes.center}`}>Needs Review</th>
              <th className={`${classes.th} ${classes.center}`} title={LEASES_HELP}>
                Leases
              </th>
              <th className={classes.th}>Worker</th>
              <th className={classes.th}>Retimer</th>
              <th className={`${classes.th} ${classes.center}`}>Webhook</th>
              <th className={classes.th}>Runtime</th>
              <th className={classes.th}>Models</th>
              <th className={classes.th}>Created</th>
              <th className={classes.th}>Completed</th>
              <th className={classes.th}>Error / Artifacts</th>
            </tr>
          </thead>
          <tbody>
            {!isError &&
              data &&
              data.pages.map((page, pageIndex) => (
                <React.Fragment key={pageIndex}>
                  {page.jobs.map(job => (
                    <tr key={job.job_id} className={classes.row}>
                      <td className={classes.td}>
                        <button
                          className={classes.songLink}
                          onClick={() => navigate('/edit-song/' + job.song_f_id)}
                          title={`open edit-song screen for ${job.song_f_id}\n${job.job_id}`}
                        >
                          <div className={classes.songName}>
                            {job.song_name || job.song_f_id}
                          </div>
                          {job.song_artist_name && (
                            <div className={classes.songArtist}>{job.song_artist_name}</div>
                          )}
                          <div className={classes.songFid}>{job.song_f_id}</div>
                        </button>
                      </td>
                      <td className={classes.td}>
                        <span
                          className={`${classes.badge} ${classes['badge_' + job.status] || ''}`}
                        >
                          {job.status === 'leased' ? 'active' : job.status}
                        </span>
                      </td>
                      <td className={classes.td}>{job.tier || '—'}</td>
                      <td className={classes.td}>{job.language || '—'}</td>
                      <td className={`${classes.td} ${classes.center}`}>
                        {job.flag_count ?? '—'}
                      </td>
                      <td className={`${classes.td} ${classes.center}`}>
                        {job.check_lyrics == null ? (
                          '—'
                        ) : job.check_lyrics ? (
                          <span className={classes.review}>yes</span>
                        ) : (
                          'no'
                        )}
                      </td>
                      <td className={`${classes.td} ${classes.center}`} title={LEASES_HELP}>
                        {(job.attempts ?? 0) >= 3 ? (
                          <span className={classes.review}>⚠ {job.attempts}</span>
                        ) : (
                          <span className={classes.muted}>·</span>
                        )}
                      </td>
                      <td className={classes.td}>
                        {job.leased_by ? (
                          <span className={classes.mono}>{job.leased_by}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className={classes.td}>{job.retimer_requested || '—'}</td>
                      <td className={`${classes.td} ${classes.center}`}>
                        {job.webhook_delivered == null ? (
                          '—'
                        ) : job.webhook_delivered ? (
                          <span className={classes.ok}>delivered</span>
                        ) : (
                          <span className={classes.muted}>pending</span>
                        )}
                      </td>
                      <td className={classes.td}>{formatRuntime(job.runtime_seconds)}</td>
                      <td className={classes.td}>
                        {modelRuntimeEntries(job.model_runtimes).length === 0
                          ? '—'
                          : modelRuntimeEntries(job.model_runtimes).map(([name, secs]) => (
                              <span
                                key={name}
                                className={classes.metaChip}
                                title={`${name}: ${secs}`}
                              >
                                {name} {formatRuntime(secs)}
                              </span>
                            ))}
                      </td>
                      <td className={classes.td}>{formatDateTime(job.created_at)}</td>
                      <td className={classes.td}>{formatDateTime(job.completed_at)}</td>
                      <td className={`${classes.td} ${classes.errorCell}`}>
                        {job.error && (
                          <div className={classes.error} title={job.error}>
                            {job.error}
                          </div>
                        )}
                        {artifactEntries(job.artifact_uris).map(([k, v]) => {
                          const href = toBrowsableUri(v)
                          return href ? (
                            <a
                              key={k}
                              className={classes.artifact}
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              title={typeof v === 'string' ? v : JSON.stringify(v)}
                            >
                              {k}
                            </a>
                          ) : (
                            <span
                              key={k}
                              className={classes.artifact}
                              title={typeof v === 'string' ? v : JSON.stringify(v)}
                            >
                              {k}
                            </span>
                          )
                        })}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
          </tbody>
        </table>
        <div ref={ref} className={classes.sentinel} />
        {!isLoading && !isError && jobsCount === 0 && (
          <div className={classes.empty}>No jobs found.</div>
        )}
      </div>

      <Loader isLoading={isLoading || isFetchingNextPage} />

      {isError && (
        <h1 className={classes.errorText}>{`Failed to fetch jobs with code: ${
          error?.status ? error.status : 'unknown'
        }`}</h1>
      )}
    </div>
  )
}
